import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { logCustomerDeletion } from "../utils/loggingWrapper";
import * as XLSX from "xlsx";

const STORAGE_KEY = 'customersListState';
const CACHE_KEY = 'customersDataCache';
const CACHE_DURATION = 30000; // 30 seconds cache duration

const CustomersListWithLogging = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const scrollContainerRef = useRef(null);
  const location = useLocation();
  const isFetchingRef = useRef(false);
  const intervalRef = useRef(null);

  // Restore state from sessionStorage on mount
  const getStoredState = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error restoring state:', e);
    }
    return null;
  };

  // Get cached users data
  const getCachedUsers = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        // Return cached data if it's less than CACHE_DURATION old
        if (now - timestamp < CACHE_DURATION) {
          return data;
        }
      }
    } catch (e) {
      console.error('Error reading cache:', e);
    }
    return null;
  };

  // Clear old cache entries to free up space
  const clearOldCache = () => {
    try {
      const cacheKeys = ['customersDataCache', 'servicemenDataCache', 'dashboardDataCache', 'dashboardStatsCache', 'bookingsDataCache'];
      cacheKeys.forEach(key => {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const { timestamp } = JSON.parse(cached);
            const now = Date.now();
            // Clear cache older than 1 minute
            if (now - timestamp > 60000) {
              localStorage.removeItem(key);
            }
          }
        } catch (e) {
          // Ignore errors for individual cache entries
        }
      });
    } catch (e) {
      // Ignore errors
    }
  };

  // Save users to cache with quota handling
  const saveToCache = (usersData) => {
    try {
      // Try to clear old cache first
      clearOldCache();
      
      // Reduce data size by only storing essential fields
      const reducedData = usersData.map(item => ({
        id: item.id,
        name: item.name,
        email: item.email,
        phone: item.phone,
        createdAt: item.createdAt,
        registeredAt: item.registeredAt,
        lastSeen: item.lastSeen,
        active: item.active,
        city: item.city
      }));

      const cacheData = {
        data: reducedData,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        // Clear all cache and try again
        try {
          localStorage.removeItem(CACHE_KEY);
          localStorage.removeItem('servicemenDataCache');
          localStorage.removeItem('dashboardDataCache');
          localStorage.removeItem('bookingsDataCache');
          // Don't retry, just fail silently
        } catch (clearError) {
          // Ignore clear errors
        }
      }
      // Silently fail - app will work without cache
    }
  };

  const storedState = getStoredState();
  const [currentPage, setCurrentPage] = useState(storedState?.currentPage || 1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [activeFilter, setActiveFilter] = useState("All");
  const [rangeFilter, setRangeFilter] = useState("total"); // today | last7 | month | total
  const [customStartDate, setCustomStartDate] = useState(''); // yyyy-mm-dd
  const [customEndDate, setCustomEndDate] = useState(''); // yyyy-mm-dd
  const usersPerPage = 20;
  const DEFAULT_IMAGE = "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";
  const navigate = useNavigate();
  const { hasPermission, PERMISSIONS, user } = useAuth();

  const fetchUsers = async (showLoader = true) => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      if (showLoader) {
        setLoading(true);
      }

      const response = await fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/users");
      const data = await response.json();
      const usersArray = Array.isArray(data) ? data : [data];
      
      setUsers(usersArray);
      saveToCache(usersArray);
      
      if (showLoader) {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      // Don't clear users on error if we have cached data
      const cached = getCachedUsers();
      if (!cached) {
        setUsers([]);
      }
      if (showLoader) {
        setLoading(false);
      }
    } finally {
      isFetchingRef.current = false;
    }
  };

  // Save state to sessionStorage
  const saveState = () => {
    try {
      const state = {
        currentPage,
        scrollPosition: scrollContainerRef.current?.scrollTop || window.scrollY || 0
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Error saving state:', e);
    }
  };

  // Save scroll position on scroll
  useEffect(() => {
    const container = scrollContainerRef.current || window;
    const handleScroll = () => {
      const scrollPos = scrollContainerRef.current?.scrollTop || window.scrollY || 0;
      try {
        const state = {
          currentPage,
          scrollPosition: scrollPos
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        // Ignore storage errors
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [currentPage]);

  // Save state when page changes
  useEffect(() => {
    saveState();
  }, [currentPage]);

  useEffect(() => {
    // Load cached data immediately for instant display
    const cachedUsers = getCachedUsers();
    if (cachedUsers) {
      setUsers(cachedUsers);
      setLoading(false);
      // Fetch fresh data in background without showing loader
      fetchUsers(false);
    } else {
      // No cache, fetch with loader
      fetchUsers(true);
    }

    // Smart polling: only poll when tab is visible and reduce frequency
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, clear interval
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        // Tab is visible, start polling
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            fetchUsers(false); // Background refresh without loader
          }, 30000); // Poll every 30 seconds instead of 5
        }
      }
    };

    // Start polling if tab is visible
    if (!document.hidden) {
      intervalRef.current = setInterval(() => {
        fetchUsers(false); // Background refresh without loader
      }, 30000); // Poll every 30 seconds instead of 5
    }

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Restore scroll position after data loads
  useEffect(() => {
    if (!loading && storedState) {
      // Use setTimeout to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        const scrollPos = storedState.scrollPosition || 0;
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPos;
        } else {
          window.scrollTo(0, scrollPos);
        }
        // Clear the stored state after restoring
        sessionStorage.removeItem(STORAGE_KEY);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [loading, storedState]);

  // Pre-fill search from ?phone= or ?id= when opened from Account Delete Requests (e.g. new tab)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const phone = params.get("phone");
    const id = params.get("id");
    if (phone != null && String(phone).trim() !== "") {
      setSearchTerm(String(phone).trim());
      setCurrentPage(1);
    } else if (id != null && String(id).trim() !== "") {
      setSearchTerm(String(id).trim());
      setCurrentPage(1);
    }
  }, [location.search]);

  // Helper function to convert Firebase Timestamp to Date (same as Dashboard)
  const toDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    // Handle Firestore Timestamp with toDate method
    if (val && typeof val.toDate === 'function') return val.toDate();
    if (typeof val === "number") return new Date(val);
    if (typeof val === "string") {
      // Try parsing the string format "22 December 2025 at 21:28:38 UTC+5:30"
      // Replace "at" with a space and try to parse
      const normalizedStr = val.replace(/\s+at\s+/i, ' ');
      const d = new Date(normalizedStr);
      if (!isNaN(d.getTime())) return d;
      // Fallback to original string parsing
      const d2 = new Date(val);
      return isNaN(d2.getTime()) ? null : d2;
    }
    // Firestore timestamp formats
    if (val._seconds) return new Date(val._seconds * 1000);
    if (val.seconds) return new Date(val.seconds * 1000);
    if (val.toMillis) return new Date(val.toMillis());
    if (val.createdAt?._seconds) return new Date(val.createdAt._seconds * 1000);
    return null;
  };

  // Helper function to check if two dates are the same day
  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  // Compute effective date range based on rangeFilter and optional custom dates (same as Dashboard)
  const getEffectiveRange = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayMs = 24 * 60 * 60 * 1000;
    const defaultRanges = {
      today: { start: startOfToday, end: new Date(startOfToday.getTime() + dayMs) },
      last7: { start: new Date(startOfToday.getTime() - 6 * dayMs), end: new Date(startOfToday.getTime() + dayMs) },
      month: { start: new Date(now.getFullYear(), now.getMonth(), 1), end: new Date(now.getFullYear(), now.getMonth() + 1, 1) },
      total: { start: new Date(0), end: new Date(8640000000000000) },
    };

    let { start, end } = defaultRanges[rangeFilter] || defaultRanges.total;

    // If last7, month, or total and custom dates provided, override
    if ((rangeFilter === 'last7' || rangeFilter === 'month' || rangeFilter === 'total')) {
      const hasStart = customStartDate && customStartDate.trim() !== '';
      const hasEnd = customEndDate && customEndDate.trim() !== '';
      if (hasStart) {
        const s = new Date(customStartDate + 'T00:00:00');
        if (!isNaN(s.getTime())) start = s;
      }
      if (hasEnd) {
        const e = new Date(customEndDate + 'T23:59:59.999');
        if (!isNaN(e.getTime())) end = e;
      }
    }
    return { start, end };
  };

  // Helper function to filter by date period (updated to match Dashboard logic)
  const filterByDate = (user) => {
    // Use the same field as Dashboard: createdAt || registeredAt
    const dateAccessor = user.createdAt || user.registeredAt;
    const userDate = toDate(dateAccessor) || toDate(user.createdAt) || toDate(user.registeredAt) || toDate(user.lastSeen);

    // Check if custom dates are provided
    const hasCustomStart = customStartDate && customStartDate.trim() !== '';
    const hasCustomEnd = customEndDate && customEndDate.trim() !== '';
    const hasCustomDates = hasCustomStart || hasCustomEnd;

    // If custom dates are provided, use getEffectiveRange (which handles custom dates)
    if (hasCustomDates) {
      if (!userDate) return false; // Exclude users without dates when custom range is specified
      const { start, end } = getEffectiveRange();
      return userDate >= start && userDate < end;
    }

    // When "Total" is selected without custom dates, show all customers (including those without dates)
    if (rangeFilter === 'total') {
      return true;
    }

    // For specific date ranges without custom dates, only include customers that have valid dates
    if (!userDate) return false;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayMs = 24 * 60 * 60 * 1000;

    // Match Dashboard's computeCounts logic exactly for predefined ranges
    if (rangeFilter === 'today') {
      return isSameDay(userDate, now);
    } else if (rangeFilter === 'last7') {
      const startOfWeek = new Date(startOfToday.getTime() - 6 * dayMs);
      return userDate >= startOfWeek;
    } else if (rangeFilter === 'month') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return userDate >= startOfMonth;
    }

    // Fallback
    return true;
  };

  const filteredUsers = useMemo(() => {
    return users
      .filter(user => {
        // Search filter
        // Phone number search - convert to string and check if phone contains search term
        const phoneStr = String(user.phone || "").trim();
        const searchStr = String(searchTerm || "").trim();
        const phoneMatches = searchStr.length > 0 && phoneStr.length > 0 && phoneStr.includes(searchStr);

        const matchesSearch = (user.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.id || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          phoneMatches;

        // Active filter
        let matchesActive = true;
        if (activeFilter === "Active") {
          matchesActive = user.active === true;
        } else if (activeFilter === "Inactive") {
          matchesActive = user.active === false;
        }

        // Date filter
        const matchesDate = filterByDate(user);

        return matchesSearch && matchesActive && matchesDate;
      })
      .sort((a, b) => {
        if (sortBy === "id") return a.id.localeCompare(b.id);
        if (sortBy === "createdAt") {
          const aDate = toDate(a.createdAt);
          const bDate = toDate(b.createdAt);

          // Customers without dates appear last
          if (!aDate && !bDate) return (a.name || "").localeCompare(b.name || "");
          if (!aDate) return 1;
          if (!bDate) return -1;

          // Sort by newest first (descending order)
          return bDate.getTime() - aDate.getTime();
        }
        if (sortBy === "lastSeen") {
          const aTime = a.lastSeen?._seconds || 0;
          const bTime = b.lastSeen?._seconds || 0;
          return bTime - aTime;
        }
        if (sortBy === "device") {
          const aDevice = a.deviceType || "";
          const bDevice = b.deviceType || "";

          // Handle N/A to appear last
          if (aDevice === "" || aDevice === "N/A") return 1;
          if (bDevice === "" || bDevice === "N/A") return -1;

          return aDevice.localeCompare(bDevice);
        }
        if (sortBy === "accountEdited") {
          const aEdited = a.accountEdited === true;
          const bEdited = b.accountEdited === true;
          if (aEdited && !bEdited) return -1;
          if (!aEdited && bEdited) return 1;
          return (a.name || "").localeCompare(b.name || "");
        }
        return (a[sortBy] || "").localeCompare(b[sortBy] || "");
      });
  }, [users, searchTerm, sortBy, activeFilter, rangeFilter, customStartDate, customEndDate]);

  const paginatedUsers = useMemo(() => {
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    return {
      indexOfFirstUser,
      currentUsers: filteredUsers.slice(indexOfFirstUser, indexOfLastUser),
      totalPages: Math.ceil(filteredUsers.length / usersPerPage),
    };
  }, [filteredUsers, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of table when page changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate pagination page numbers with sliding window
  const getPaginationPages = () => {
    const totalPages = paginatedUsers.totalPages;
    const current = currentPage;
    const maxVisible = 10; // Maximum number of page buttons to show
    const pages = [];

    if (totalPages <= maxVisible) {
      // If total pages is less than max visible, show all
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate window around current page
      let start = Math.max(2, current - Math.floor(maxVisible / 2));
      let end = Math.min(totalPages - 1, start + maxVisible - 3);

      // Adjust start if we're near the end
      if (end === totalPages - 1) {
        start = Math.max(2, totalPages - maxVisible + 2);
      }

      // Add ellipsis if there's a gap after first page
      if (start > 2) {
        pages.push('ellipsis-start');
      }

      // Add pages in the window
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if there's a gap before last page
      if (end < totalPages - 1) {
        pages.push('ellipsis-end');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setCurrentPage(1);
  };

  const handleActiveFilterChange = (e) => {
    setActiveFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleView = (user) => {
    saveState();
    navigate("/profile", { state: { user, fromList: true } });
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      // Find the user to get their name for logging
      const userToDelete = users.find(u => u.id === userId);
      const userName = userToDelete?.name || userToDelete?.email || 'Unknown';

      const response = await fetch(
        `https://us-central1-workexpress-1ffff.cloudfunctions.net/app/users/${userId}`,
        { method: "DELETE" }
      );

      if (!response.ok) throw new Error("Failed to delete user");

      // Update local state
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));

      // Log the activity
      await logCustomerDeletion(user, userId, userName);

      alert("User deleted successfully");
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user");
    }
  };

  const handleResetAccountEdited = async (userId) => {
    if (!window.confirm("Set accountEdited to false for this customer?")) return;

    try {
      const response = await fetch(
        `https://us-central1-workexpress-1ffff.cloudfunctions.net/app/users/${userId}/accountEdited`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accountEdited: false }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to reset accountEdited");
      }

      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === userId ? { ...u, accountEdited: false } : u
        )
      );
      alert("accountEdited set to false successfully");
    } catch (error) {
      console.error("Error resetting accountEdited for user:", error);
      alert("Failed to reset accountEdited");
    }
  };

  const handleExport = () => {
    try {
      // Get the currently filtered and sorted data
      const dataToExport = filteredUsers.map((user, index) => ({
        "Serial No": index + 1,
        "Name": user.name || "N/A",
        "Email": user.email || "N/A",
        "Phone": user.phone || "N/A",
        "City": user.city || "N/A",
        "Device Type": user.deviceType && user.deviceType.trim() !== '' ? user.deviceType : "N/A"
      }));

      // Create a new workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dataToExport);

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, "Customers");

      // Generate filename with current date
      const filename = `Customers_Export_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Write the file
      XLSX.writeFile(wb, filename);

      alert(`Exported ${dataToExport.length} records to ${filename}`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Failed to export data");
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <span className="loader"></span>
        <style>{`
          .loading {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            color: #666;
          }
          .loader {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            border: 2px solid #FF3D00;
            box-sizing: border-box;
            animation: rotation 1s linear infinite;
          }
          .loader::after {
            content: '';  
            box-sizing: border-box;
            position: absolute;
            left: 4px;
            top: 4px;
            border: 2px solid #000;
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          [data-theme="dark"] .loader::after {
            border: 2px solid #FFF;
          }
          @keyframes rotation {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="customers-panel" ref={scrollContainerRef}>
      <style>{`
        .customers-panel {
          padding: 30px;
          margin: 0 auto;
          background: #f9fafb;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid #e5e7eb;
          flex-wrap: wrap;
          gap: 15px;
        }
        h2 {
          color: #1f2937;
          font-size: 20px;
          margin: 0;
        }
        .search-sort {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }
        .search-input {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 14px;
          width: 200px;
          transition: all 0.2s;
        }
        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 4px rgba(59,130,246,0.3);
        }
        .sort-btn {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: #fff;
          font-size: 14px;
          cursor: pointer;
          color: #374151;
        }
        .export-btn {
          padding: 8px 16px;
          border: 1px solid #3b82f6;
          border-radius: 4px;
          background: #3b82f6;
          font-size: 14px;
          cursor: pointer;
          color: #fff;
          font-weight: 500;
          transition: all 0.2s;
        }
        .export-btn:hover {
          background: #2563eb;
          border-color: #2563eb;
        }
        .table-container {
          overflow-x: auto;
          min-height: 300px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          background: #f3f4f6;
          color: #4a5568;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          font-size: 14px;
          border-bottom: 2px solid #e5e7eb;
          position: sticky;
          top: 0;
        }
        td {
          padding: 12px;
          border-bottom: 1px solid #e5e7eb;
          font-size: 14px;
          color: #4a5568;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .profile-pic {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          object-fit: cover;
          margin-right: 8px;
          vertical-align: middle;
          display: inline-block;
        }
        .status {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          display: inline-block;
        }
        .status.active {
          background: #dcfce7;
          color: #166534;
        }
        .status.inactive {
          background: #fee2e2;
          color: #991b1b;
        }
        .customer-row {
          transition: background 0.2s;
        }
        .customer-row:hover {
          background: #f9fafb;
        }
        .actions {
          display: flex;
          gap: 8px;
        }
        .action-btn {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: 1px solid #d1d5db;
          background: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2px;
          transition: background 0.2s;
        }
        .action-btn.view-btn:hover {
          background: #00FF00;
        }
        .action-btn.view-btn:hover svg {
          stroke: #fff;
        }
        .action-btn.delete-btn:hover {
          background: #FF0000;
        }
        .action-btn.delete-btn:hover svg {
          stroke: #fff;
        }
        .pagination {
          margin-top: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #4a5568;
          font-size: 14px;
        }
        .pagination-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          max-width: 100%;
        }
        .page-btn {
          padding: 6px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: #fff;
          cursor: pointer;
          color: #374151;
          transition: background 0.3s ease;
          min-width: 40px;
          text-align: center;
          white-space: nowrap;
        }
        .page-btn:hover:not(.active):not(:disabled) {
          background: #e5e7eb;
        }
        .page-btn.active {
          background: #3b82f6;
          color: #fff;
          border-color: #3b82f6;
        }
        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .page-ellipsis {
          padding: 6px 4px;
          color: #6b7280;
          font-weight: 500;
          user-select: none;
        }
        .no-results {
          text-align: center;
          padding: 20px;
          color: #6b7280;
        }
        @media (max-width: 768px) {
          .header {
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
          }
          .search-sort {
            width: 100%;
          }
          .search-input, .sort-btn, .export-btn {
            width: 100%;
          }
        }
      `}</style>
      <div className="header">
        <h2>Customers List</h2>
        <div className="search-sort">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page when search changes
            }}
          />
          <select className="sort-btn" value={sortBy} onChange={handleSortChange}>
            <option value="createdAt">Sort By Created Date</option>
            <option value="id">Sort By ID</option>
            <option value="name">Sort By Name</option>
            <option value="email">Sort By Email</option>
            <option value="lastSeen">Sort By Last Seen</option>
            <option value="device">Sort By Device</option>
            <option value="accountEdited">Sort By Account Edited</option>
          </select>
          <select className="sort-btn" value={activeFilter} onChange={handleActiveFilterChange}>
            <option value="All">All Status</option>
            <option value="Active">Active Only</option>
            <option value="Inactive">Inactive Only</option>
          </select>
          {(rangeFilter === 'last7' || rangeFilter === 'month' || rangeFilter === 'total') && (
            <>
              <input
                type="date"
                className="sort-btn"
                value={customStartDate}
                onChange={(e) => { setCustomStartDate(e.target.value); setCurrentPage(1); }}
                style={{ width: '150px' }}
                placeholder="Start Date"
              />
              <input
                type="date"
                className="sort-btn"
                value={customEndDate}
                onChange={(e) => { setCustomEndDate(e.target.value); setCurrentPage(1); }}
                style={{ width: '150px' }}
                placeholder="End Date"
              />
            </>
          )}
          <select
            className="sort-btn"
            value={rangeFilter}
            onChange={(e) => {
              const newFilter = e.target.value;
              setRangeFilter(newFilter);
              setCurrentPage(1);
              // Clear custom dates when switching to "today" since it doesn't use custom dates
              if (newFilter === 'today') {
                setCustomStartDate('');
                setCustomEndDate('');
              }
            }}
          >
            <option value="today">Today</option>
            <option value="last7">Last 7 Days</option>
            <option value="month">This Month</option>
            <option value="total">Total</option>
          </select>
          <button className="export-btn" onClick={handleExport} title="Export to Excel">
            Export
          </button>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Customer</th>
              <th>Email</th>
              <th>Phone</th>
              <th>City</th>
              <th>Last Seen</th>
              <th>Device Type</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.currentUsers.length > 0 ? (
              paginatedUsers.currentUsers.map((user, index) => {
                const imageSrc = user.image
                  ? user.image.startsWith("data:image")
                    ? user.image
                    : `data:image/jpeg;base64,${user.image}`
                  : DEFAULT_IMAGE;

                return (
                  <tr key={user.id} className="customer-row">
                    <td>{paginatedUsers.indexOfFirstUser + index + 1}</td>
                    <td>
                      <img
                        src={imageSrc}
                        alt={`${user.name || "User"} profile`}
                        className="profile-pic"
                        onError={(e) => (e.target.src = DEFAULT_IMAGE)}
                      />
                      {user.name || "N/A"}
                    </td>
                    <td>{user.email || "N/A"}</td>
                    <td>{user.phone || "N/A"}</td>
                    <td>{user.city || "N/A"}</td>
                    <td>
                      {user.lastSeen?._seconds
                        ? new Date(user.lastSeen._seconds * 1000).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td>{user.deviceType && user.deviceType.trim() !== '' ? user.deviceType : "N/A"}</td>
                    <td>
                      <span className={`status ${user.active ? "active" : "inactive"}`}>
                        {user.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="actions">
                      {hasPermission(PERMISSIONS.CUSTOMERS.VIEW) && (
                        <button className="action-btn view-btn" onClick={() => handleView(user)} title="View">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#00FF00"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-label="view"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      )}
                      {hasPermission(PERMISSIONS.CUSTOMERS.DELETE) && (
                        <button className="action-btn delete-btn" onClick={() => handleDelete(user.id)} title="Delete">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#FF0000"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-label="delete"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      )}
                      {/* Show reset accountEdited button only when field is true */}
                      {user.accountEdited === true && (
                        <button
                          className="action-btn reset-btn"
                          onClick={() => handleResetAccountEdited(user.id)}
                          title="Set accountEdited to false"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#F59E0B"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-label="reset-account-edited"
                          >
                            <polyline points="1 4 1 10 7 10" />
                            <path d="M3.51 15a9 9 0 1 0 .49-5" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="no-results">
                  No customers found matching your search
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <span>Showing {filteredUsers.length} Entries →</span>
        <div className="pagination-buttons">
          <button
            className="page-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          {getPaginationPages().map((page, index) => {
            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
              return (
                <span key={`ellipsis-${index}`} className="page-ellipsis">
                  ...
                </span>
              );
            }
            return (
              <button
                key={page}
                className={`page-btn ${currentPage === page ? "active" : ""}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            );
          })}
          <button
            className="page-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === paginatedUsers.totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomersListWithLogging;
